import { zodResolver } from '@hookform/resolvers/zod';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import { IconButton, Tooltip } from '@mui/material';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import ErrorSection from 'components/ErrorSection/ErrorSection';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import type { NoopT } from 'declarations/types/common';
import { formatTooltipWithShortcut } from 'helpers/formatTooltipWithShortcut';
import { CloseIcon, SendIcon } from 'icons';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import { useMessages } from '../../contexts/MessagesContext';

import type { NewTicketMessageFormValuesT } from './declarations';
import useMessageForm from './hooks/useMessageForm';
import {
  CloseIconBtnStyled,
  FormWrapStyled,
  InputImageWrapStyled,
  InputMessageWrapStyled,
  SendMessageBtnStyled,
  StyledDragDropInput,
  TextAreaStyled,
} from './MessageForm.styled';
import validationSchema from './validationSchema';

export type MessageFormPropsT = {
  roofTopName: string;
  requiresAttachment: boolean;
  assignToWarrCloud: boolean;
  handleOpenAddAttachmentPopup: NoopT;
};

const MessageForm: FC<MessageFormPropsT> = ({
  requiresAttachment,
  assignToWarrCloud,
  handleOpenAddAttachmentPopup,
}) => {
  const dispatch = useAppDispatch();
  const {
    unsavedMessage,
    isCreatingMessage,
    isMessageCreationError,
    createMessageErrorMessage,
    createMessageValidationErrorMessage,
    createScreenshotMessageErrorMessage,
    createScreenShotMessageValidationErrorMessage,
  } = useMessages();
  const { handleSubmitForm } = useMessageForm(assignToWarrCloud);
  const fromMethods = useForm<NewTicketMessageFormValuesT>({
    defaultValues: {
      message: '',
      file: null,
    },
    resolver: zodResolver(validationSchema),
    mode: 'onChange',
  });

  const { handleSubmit, reset } = fromMethods;
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);

  const handleDrop = (acceptedFiles: File[]): void => {
    const file = acceptedFiles[0];
    if (file.type === 'image/jpeg' || file.type === 'image/png') {
      fromMethods.setValue('file', file, { shouldValidate: true, shouldDirty: true });
      const reader = new FileReader();
      reader.onload = (e): void => setPreviewImage(e.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      dispatch(
        updateSnackbarNotification({
          snackbarMessage: 'Only JPEG and PNG files are allowed.',
          snackBarSeverity: 'warning',
        })
      );
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDropEvent = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    if (!requiresAttachment) return;
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) {
      handleDrop(files);
    }
  };

  const handleImagePaste = (event: React.ClipboardEvent<HTMLDivElement>): void => {
    if (!requiresAttachment) return;
    const pastedData = event.clipboardData.items;
    if (pastedData) {
      const imageItem = pastedData[0];
      if (imageItem.kind === 'file') {
        const file = imageItem.getAsFile();
        if (file?.type === 'image/jpeg' || file?.type === 'image/png') {
          fromMethods.setValue('file', file, { shouldValidate: true, shouldDirty: true });
          const reader = new FileReader();
          reader.onload = (e): void => setPreviewImage(e.target?.result as string);
          reader.readAsDataURL(file);
        }
      }
    }
  };

  const handleRemoveImage = (): void => {
    setPreviewImage(null);
    fromMethods.setValue('file', null, { shouldValidate: true, shouldDirty: true });
  };

  useEffect((): void => {
    if (unsavedMessage) {
      reset();
      setPreviewImage(null);
    }
  }, [unsavedMessage, reset]);

  return (
    <>
      {isMessageCreationError ? (
        <FlexContainer direction="column" align="center" justify="center">
          <ErrorSection
            errorDetails={
              createMessageErrorMessage ||
              createMessageValidationErrorMessage ||
              createScreenshotMessageErrorMessage ||
              createScreenShotMessageValidationErrorMessage
            }
          />
        </FlexContainer>
      ) : (
        <FormProvider {...fromMethods}>
          <FormWrapStyled>
            <Tooltip title={formatTooltipWithShortcut('Add attachment')} arrow>
              <span>
                <IconButton
                  aria-label="Attach"
                  disabled={!requiresAttachment}
                  onClick={handleOpenAddAttachmentPopup}
                  sx={{ color: (t): string => t.palette.primary.main }}
                >
                  <AttachFileIcon />
                </IconButton>
              </span>
            </Tooltip>
            <InputMessageWrapStyled
              ref={dropzoneRef}
              onPaste={handleImagePaste}
              onDragOver={handleDragOver}
              onDrop={handleDropEvent}
            >
              <TextAreaStyled
                rows={1}
                name="message"
                placeholder={'Send a message to Dealer'}
                disabled={isCreatingMessage}
              />

              {previewImage && !isCreatingMessage ? (
                <InputImageWrapStyled>
                  <img
                    src={previewImage}
                    alt="Preview"
                    style={{ maxHeight: '100px', maxWidth: '100%' }}
                  />
                  <CloseIconBtnStyled
                    variant="transparent"
                    icon={<CloseIcon />}
                    title="Remove image"
                    onClick={handleRemoveImage}
                  />
                </InputImageWrapStyled>
              ) : null}
              <StyledDragDropInput />
            </InputMessageWrapStyled>
            <SendMessageBtnStyled
              type="button"
              aria-busy={isCreatingMessage}
              aria-label={isCreatingMessage ? 'Sending message' : 'Create message'}
              variant="outline"
              disabled={isCreatingMessage}
              icon={<SendIcon />}
              title={isCreatingMessage ? 'Sending…' : 'Send a message'}
              onClick={handleSubmit(handleSubmitForm)}
            />
          </FormWrapStyled>
        </FormProvider>
      )}
    </>
  );
};

export default MessageForm;
